// inv/src/components/blog/SinglePost.jsx
import { useEffect, useMemo, useState } from "react";
import { useInitialData } from "../data/DataContext";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../config";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../seo/breadcrumbs";
import RelatedArticles from "./RelatedArticles";
import { AlertBox } from "../../components/ui/alertBox";
import SeoHead from "../../seo/seoHead";
import CTA from "../ui/CTA";

const normalizeLang = (value = "en") => String(value || "en").split("-")[0].toLowerCase();

const SinglePost = () => {
  const initialData = useInitialData();
  const { t, i18n } = useTranslation();
  const { slug } = useParams();

  const {
    article: initialArticle = null,
    related: initialRelated = [],
    canonicalUrl = "",
    lang: initialLang = "en-US",
    mdx_html: ssrMdxHtml = "",
  } = initialData;

  const ssrLang = initialLang;
  const runtimeLng = normalizeLang(i18n.language || ssrLang);

  const [article, setArticle] = useState(initialArticle);
  const [otherArticles, setOtherArticles] = useState(initialRelated);

  const [mdxHtml, setMdxHtml] = useState(ssrMdxHtml);
  const [isMdxLoading, setIsMdxLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initialArticleLang = normalizeLang(initialArticle?.lang || ssrLang);
    const needFetchArticle =
      !initialArticle ||
      initialArticle.url_slug !== slug ||
      initialArticleLang !== runtimeLng;

    if (needFetchArticle) {
      fetch(`${API_URL}/articles-cdn/${slug}?lang=${encodeURIComponent(runtimeLng)}`)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setArticle(data?.error ? null : data);
        })
        .catch((error) => {
          if (cancelled) return;
          console.error(error);
          setArticle(null);
        });
    } else {
      setArticle(initialArticle);
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, runtimeLng]);

  useEffect(() => {
    let cancelled = false;

    async function loadMdx() {
      setMdxHtml("");

      if (!article?.content_url) {
        setIsMdxLoading(false);
        return;
      }
      try {
        setIsMdxLoading(true);
        const res = await fetch(`${API_URL}/mdx-render?url=${encodeURIComponent(article.content_url)}`);
        const data = await res.json();
        if (cancelled) return;

        const raw = data?.html || "";
        setMdxHtml(`<!-- CSR_MDX_BEGIN: ${slug} -->${raw}<!-- CSR_MDX_END: ${slug} -->`);
      } catch (e) {
        console.error("[SinglePost] MDX render fetch failed:", e);
        setMdxHtml("");
      } finally {
        if (!cancelled) setIsMdxLoading(false);
      }
    }

    loadMdx();
    return () => { cancelled = true; };
  }, [article?.content_url, runtimeLng, slug]);

  useEffect(() => {
    if (article && (!initialRelated.length || article.url_slug !== slug)) {
      fetch(`${API_URL}/articles-cdn?excludeSlug=${slug}&limit=5`)
        .then((r) => r.json())
        .then(({ articles }) => setOtherArticles(articles || []))
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article, slug]);

  const publishDate = article?.publish_date || null;
  const formattedDate = useMemo(() => {
    if (!publishDate) return "";
    try {
      return new Intl.DateTimeFormat(ssrLang, {
        year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC",
      }).format(new Date(publishDate));
    } catch {
      return new Date(publishDate).toISOString().slice(0, 10);
    }
  }, [publishDate, ssrLang]);

  if (!article) return <div>{t("pageBlog.content.loadingArticle")}</div>;

  return (
    <>
      <SeoHead
        lang={ssrLang}
        title={article.seo_title || article.title}
        description={article.seo_description || article.short_description}
        canonicalUrl={canonicalUrl}
        ogTitle={article.seo_title || article.title}
        ogDescription={article.seo_description || article.short_description}
        ogImage={article.img_url}
        ogUrl={initialData?.seo?.ogUrl || canonicalUrl}
        ogType="article"
        alternates={initialData?.seo?.alternates}
        jsonLd={initialData?.seo?.jsonLd}
      />

      <div className="mx-auto max-w-3xl px-8 my-20" key={slug}>
        <Breadcrumbs mapSegments={{ blog: t("menu.blog") }} pageTitle={article.title} />

        <h1 className="mt-12 !text-3xl sm:!text-4xl leading-10">{article.title}</h1>
        <p className="my-6">{formattedDate}</p>

        {article.img_url && (
          <figure className="my-6 rounded-xl overflow-hidden border border-gray-200">
            <img
              src={article.img_url}
              alt={article.seo_alt || article.title || "Article image"}
              loading="eager"
              fetchpriority="high"
              className="w-full h-auto block"
              width={1200} height={630}
            />
          </figure>
        )}

        <AlertBox className="mb-12" variant="green">
          {article.short_description && (
            <p className="text-lg" style={{ lineHeight: "1.6" }}>
              {article.short_description}
            </p>
          )}
        </AlertBox>

        <div className="my-8">
          <div className="max-w-xl">
            <CTA lng={runtimeLng} />
          </div>
        </div>

        {mdxHtml && !isMdxLoading ? (
          <div className="mdx-body" dangerouslySetInnerHTML={{ __html: mdxHtml }} />
        ) : (
          <div>{t("pageBlog.content.loadingArticle")}</div>
        )}

        <RelatedArticles currentSlug={slug} />
      </div>
    </>
  );
};

export default SinglePost;
